import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { EmptyState, Pill, Section } from '../../components/ui/index.jsx';
import { checkRequired } from '../../utils/validate.js';

// The systems organisers send event email from, as a starting point for the name
const SUGGESTIONS = ['Registration platform', 'Marketing emails', 'Exhibitor communications', 'Speaker communications'];

function AddModal({ base, eventId, data, onClose, onDone }) {
  const [form, setForm] = useState({ given_to: '', allowed_domains: '', count: '2' });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setForm({ ...form, [k]: v }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };

  const save = async () => {
    const n = Number(form.count);
    const found = Object.fromEntries(Object.entries({
      given_to: checkRequired(form.given_to, 'which list or system this is for', { max: 200 }),
      count: Number.isInteger(n) && n >= 1 && n <= data.max_contacts ? '' : `Between 1 and ${data.max_contacts}.`,
    }).filter(([, m]) => m));
    if (Object.keys(found).length) { setErrors(found); return; }
    setBusy(true); setError('');
    try {
      const res = await api(`${base}/plantings`, { method: 'POST', body: { ...form, count: n, event_id: eventId, purpose: 'own_lists' } });
      onDone(res.id);
    } catch (err) { setErrors(err.data?.fields || {}); setError(err.data?.fields ? '' : err.message); setBusy(false); }
  };

  return (
    <Modal title="Add contacts to your own list" onClose={onClose} onSubmit={save} busy={busy}
      description="We make up a couple of realistic attendees with addresses that only we receive. Add them to the list, and from then on we get a copy of every email you send to it."
      footer={<ModalActions submitLabel="Make the contacts" busyLabel="Making…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="Which list or system" required maxLength={200} value={form.given_to} onChange={(e) => set('given_to', e.target.value)}
        error={errors.given_to} placeholder="e.g. Cvent registrations, Mailchimp newsletter"
        hint={!errors.given_to ? 'Make one set per system: if a set is ever emailed by someone else, you’ll know which system it leaked from.' : undefined} />
      <div className="suggest-chips" role="group" aria-label="Suggestions">
        {SUGGESTIONS.map((s) => <button key={s} type="button" className="chip-button" onClick={() => set('given_to', s)}>{s}</button>)}
      </div>
      <Field label="Other domains it sends from" optional value={form.allowed_domains} onChange={(e) => set('allowed_domains', e.target.value)}
        error={errors.allowed_domains} placeholder="eventbrite.com" autoCapitalize="none"
        hint={!errors.allowed_domains ? 'Your own domains always count. If this system sends from its own domain on your behalf (many registration platforms do), add it, so those emails aren’t taken for a leak.' : undefined} />
      <Field label="How many contacts" type="number" min={1} max={data.max_contacts} value={form.count} onChange={(e) => set('count', e.target.value)}
        error={errors.count} hint={!errors.count ? 'Two is plenty.' : undefined} />
    </Modal>
  );
}

/**
 * "Watch your own emails": made-up contacts the company adds to its OWN registration and
 * marketing lists. We then receive its real emails, prove where they came from and keep a
 * record — so a copy anyone forwards to us is confirmed genuine, and mail to those
 * contacts from anyone else shows the list has leaked.
 */
export default function OwnListsSection({ organisationId, eventId }) {
  const base = `/portal/organisations/${organisationId}`;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api(`${base}/plantings?event_id=${eventId}`)); } catch (err) { setError(err.message); }
  }, [base, eventId]);
  useEffect(() => { load(); }, [load]);

  const own = (data?.plantings || []).filter((p) => p.purpose === 'own_lists');
  const button = data?.available && <button type="button" className="btn btn-primary btn-small" onClick={() => setAdding(true)}>Add contacts to a list</button>;
  return (
    <Section title="Watch your own emails"
      description="Add a couple of made-up attendees to your registration platform and your marketing email lists. We then receive a copy of every email you send, prove it came from you, and keep a record: when an attendee forwards us one of your emails, we can tell them it’s genuine — even from a plain Forward. If anyone else ever emails those contacts, that list has leaked."
      actions={own.length > 0 && button} flush={own.length > 0}>
      {error && <Alert tone="danger">{error}</Alert>}
      {!data ? <p className="muted">Loading…</p> : own.length === 0 ? (
        <EmptyState title="Not watching any of your lists yet" action={button}>
          {data.available
            ? 'Start with your registration platform and your marketing tool. It takes a minute: we make the contacts, you paste them in.'
            : 'This isn’t available yet. It will be soon.'}
        </EmptyState>
      ) : (
        <div className="table-scroll">
          <table className="table table-stack">
            <thead><tr><th>List</th><th>Contacts</th><th>Your emails seen</th><th>From anyone else</th></tr></thead>
            <tbody>
              {own.map((p) => (
                <tr key={p.id}>
                  <td data-label="List">
                    <Link className="strong-link" to={`/events/${eventId}/plantings/${p.id}`}>{p.given_to}</Link>
                    {p.status !== 'active' && <> <Pill>Stopped</Pill></>}
                  </td>
                  <td data-label="Contacts">{p.contacts}</td>
                  <td data-label="Your emails seen">{p.witnessed > 0 ? <Pill tone="good">{p.witnessed} recorded</Pill> : <span className="muted">None yet</span>}</td>
                  <td data-label="From anyone else">{p.leaks > 0 ? <Pill tone="bad">{p.leaks} — leaked</Pill> : <span className="muted">None</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && <AddModal base={base} eventId={eventId} data={data} onClose={() => setAdding(false)}
        onDone={(id) => navigate(`/events/${eventId}/plantings/${id}`, { state: { created: true, own: true } })} />}
    </Section>
  );
}
